import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Users, Plus, X, Mail, CheckCircle } from 'lucide-react';
import { useInviteOrgMember } from '../hooks/use-onboarding';
import type { Invitee, StepInviteTeamProps } from '../types/onboarding.types';

export function StepInviteTeam({ orgId, onComplete, onSkip, isLoading }: StepInviteTeamProps) {
  const [invitees, setInvitees] = useState<Invitee[]>([{ email: '', role: 'org_member' }]);
  const [isSending, setIsSending] = useState(false);
  const [sentInvites, setSentInvites] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const { t } = useTranslation();

  const inviteMemberMutation = useInviteOrgMember();

  const addInvitee = () => {
    setInvitees([...invitees, { email: '', role: 'org_member' }]);
  };

  const removeInvitee = (index: number) => {
    setInvitees(invitees.filter((_, i) => i !== index));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const updateInvitee = (index: number, field: keyof Invitee, value: string) => {
    setInvitees(invitees.map((inv, i) => (i === index ? { ...inv, [field]: value } : inv)));
    if (errors[index]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const sendInvitations = async () => {
    if (!orgId) return;

    const validInvitees = invitees.filter((inv) => inv.email.trim());
    if (validInvitees.length === 0) {
      onComplete();
      return;
    }

    setIsSending(true);
    const newErrors: Record<number, string> = {};

    for (let i = 0; i < invitees.length; i++) {
      const inv = invitees[i];
      if (!inv.email.trim()) continue;
      if (sentInvites.includes(inv.email)) continue;

      try {
        await inviteMemberMutation.mutateAsync({
          orgId,
          email: inv.email,
          role: inv.role,
        });
        setSentInvites((prev) => [...prev, inv.email]);
      } catch {
        newErrors[i] = t('ONBOARDING_ERRORS_FAILEDTOINVITE');
      }
    }

    setErrors(newErrors);
    setIsSending(false);

    if (Object.keys(newErrors).length === 0) {
      onComplete();
    }
  };

  const roleLabels: Record<string, string> = {
    org_admin: t('ONBOARDING_FORM_ROLEADMIN'),
    org_editor: t('ONBOARDING_FORM_ROLEEDITOR'),
    org_member: t('ONBOARDING_FORM_ROLEMEMBER'),
  };

  const roleDescriptions: Record<string, string> = {
    org_admin: t('ONBOARDING_FORM_ROLEADMINDESCRIPTION'),
    org_editor: t('ONBOARDING_FORM_ROLEEDITORDESCRIPTION'),
    org_member: t('ONBOARDING_FORM_ROLEMEMBERDESCRIPTION'),
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {t('ONBOARDING_STEPS_INVITETEAM_TITLE')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('ONBOARDING_STEPS_INVITETEAM_DESCRIPTION')}
        </p>
      </div>

      <div className="space-y-4">
        {invitees.map((invitee, index) => (
          <div
            key={index}
            className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor={`email-${index}`} className="sr-only">
                  {t('ONBOARDING_FORM_EMAIL')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id={`email-${index}`}
                    type="email"
                    value={invitee.email}
                    onChange={(e) => updateInvitee(index, 'email', e.target.value)}
                    placeholder={t('ONBOARDING_FORM_EMAILPLACEHOLDER')}
                    className="pl-10 h-11"
                    disabled={sentInvites.includes(invitee.email)}
                  />
                </div>
              </div>

              <div className="w-30">
                <Label htmlFor={`role-${index}`} className="sr-only">
                  {t('ONBOARDING_FORM_ROLE')}
                </Label>
                <Select
                  value={invitee.role}
                  onValueChange={(value) =>
                    updateInvitee(index, 'role', value as Invitee['role'])
                  }
                  disabled={sentInvites.includes(invitee.email)}
                >
                  <SelectTrigger id={`role-${index}`} className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {invitees.length > 1 && !sentInvites.includes(invitee.email) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeInvitee(index)}
                  className="h-10 w-10 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              {sentInvites.includes(invitee.email) && (
                <div className="flex h-10 w-10 items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}
            </div>

            {errors[index] && (
              <p className="text-sm text-destructive">{errors[index]}</p>
            )}

            <p className="text-xs text-muted-foreground">{roleDescriptions[invitee.role]}</p>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={addInvitee}
          className="w-full"
          disabled={isSending}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('ONBOARDING_BUTTONS_ADDANOTHER')}
        </Button>
      </div>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="ghost" onClick={onSkip} disabled={isSending || isLoading}>
          {t('ONBOARDING_BUTTONS_SKIP')}
        </Button>
        <Button
          type="button"
          onClick={sendInvitations}
          disabled={isSending || isLoading}
          className="min-w-30"
        >
          {isSending
            ? t('ONBOARDING_BUTTONS_SENDING')
            : t('ONBOARDING_BUTTONS_SENDINVITATIONS')}
        </Button>
      </div>
    </div>
  );
}
